import { useEffect, useState } from "react";
import api from "@/lib/api";
import CampaignForm from "./CampaignForm";
import { CAMPAIGN } from "@/constants/testIds";
import { Leaf, CheckCircle2, XCircle, HelpCircle, ArrowDown, ClipboardList, MessageSquare, FileSignature } from "lucide-react";
import { RootsMark } from "@/components/RootsMark";
import { track } from "@/constants/campaignDefs";

const LIME = "#B7E64B";

const HOW_ICONS = [ClipboardList, MessageSquare, FileSignature];

export default function CampaignLandingPage() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    track("landing_view");
    api.get("/campaign/config").then((r) => setConfig(r.data)).catch(() => {});
  }, []);

  const scrollToForm = () => { track("cta_click"); document.getElementById("register")?.scrollIntoView({ behavior: "smooth" }); };
  const offer = config?.offer;
  const state = config?.state || "open";
  const ctaLabel = state === "closed" ? "Registration closed" : state === "full" ? "Join the pilot waitlist" : "Register my interest";

  return (
    <div className="min-h-screen bg-[#F5F7F2]" data-testid={CAMPAIGN.landing}>
      {/* Top bar */}
      <header className="bg-[#09292D] text-white">
        <div className="mx-auto flex max-w-[1160px] items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg" style={{ backgroundColor: LIME }}>
              <RootsMark className="h-5 w-5 text-[#09292D]" />
            </div>
            <div className="leading-tight">
              <p className="font-heading text-lg font-bold">LIAS<span style={{ color: LIME }}>ROOTS</span></p>
              <p className="-mt-1 text-[10px] uppercase tracking-widest text-white/60">Pilot interest</p>
            </div>
          </div>
          <button onClick={scrollToForm} className="hidden rounded-full px-4 py-2 text-sm font-bold text-[#09292D] sm:inline-flex" style={{ backgroundColor: LIME }}>
            {ctaLabel}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-[#09292D] text-white">
        <div className="mx-auto grid max-w-[1160px] items-center gap-8 px-5 pb-14 pt-6 md:grid-cols-[3fr_2fr]">
          <div>
            {state !== "open" && (
              <span data-testid={CAMPAIGN.stateBanner} className="mb-4 inline-block rounded-full border border-white/20 bg-white/5 px-3 py-1 text-xs font-semibold uppercase tracking-wide" style={{ color: LIME }}>
                {state === "full" ? "Cohort full · waitlist open" : "Registration closed"}
              </span>
            )}
            <h1 className="font-heading text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
              {offer?.headline || "Five pilot places. Real landscaping results."}
            </h1>
            <p className="mt-4 max-w-xl text-base text-white/80 sm:text-lg">
              {offer?.subhead}
            </p>
            <button onClick={scrollToForm} data-testid={CAMPAIGN.heroCta}
                    className="mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-base font-bold text-[#09292D] transition-transform hover:scale-[1.02]"
                    style={{ backgroundColor: LIME }}>
              {ctaLabel} <ArrowDown className="h-4 w-4" />
            </button>
            <p className="mt-3 text-xs text-white/50">For selected LIAS member companies · subject to eligibility and an agreed scope</p>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] overflow-hidden rounded-2xl border border-white/10 bg-[#0f3a40]">
              <img src="https://images.unsplash.com/photo-1416879595882-3373a0480b5b?auto=format&fit=crop&w=1000&q=70"
                   alt="Landscaped greenery in Singapore (placeholder until licensed imagery is provided)"
                   className="h-full w-full object-cover opacity-90" />
            </div>
            <p className="mt-2 text-center text-[10px] text-white/40">Placeholder image — licensed Singapore landscaping photo to follow</p>
          </div>
        </div>
      </section>

      {/* Offer: inclusions / exclusions */}
      <section className="mx-auto max-w-[1160px] px-5 py-14">
        <h2 className="text-center font-heading text-2xl font-bold text-[#09292D]">What's included — and what's not</h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-slate-600">We want this to be clear and honest. Here's exactly what the pilot offers.</p>
        <div className="mt-8 grid gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-[#9ccc2f] bg-[#f3f8e6] p-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-[#09292D]"><CheckCircle2 className="h-5 w-5" /> Included</h3>
            <ul className="mt-4 space-y-3">
              {(offer?.inclusions || []).map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-[#09292D]"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#4e8a1f]" /> {t}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-slate-200 bg-white p-6">
            <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-[#09292D]"><XCircle className="h-5 w-5 text-slate-400" /> Not included</h3>
            <ul className="mt-4 space-y-3">
              {(offer?.exclusions || []).map((t, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-600"><XCircle className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" /> {t}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white">
        <div className="mx-auto max-w-[1160px] px-5 py-14">
          <h2 className="text-center font-heading text-2xl font-bold text-[#09292D]">How it works</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            {(offer?.how_it_works || []).map((s, i) => {
              const Icon = HOW_ICONS[i] || ClipboardList;
              return (
                <div key={i} className="rounded-2xl border border-slate-200 bg-[#F5F7F2] p-6">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: LIME }}>
                    <Icon className="h-5 w-5 text-[#09292D]" />
                  </div>
                  <p className="mt-3 text-xs font-bold uppercase tracking-wide text-[#4e8a1f]">Step {i + 1}</p>
                  <h3 className="font-heading text-lg font-bold text-[#09292D]">{s.title}</h3>
                  <p className="mt-1 text-sm text-slate-600">{s.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form */}
      <section id="register" className="mx-auto max-w-[1160px] px-5 py-14">
        <div className="mx-auto max-w-[680px]">
          {config ? <CampaignForm config={config} /> : <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">Loading…</div>}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="mx-auto max-w-[760px] px-5 py-14">
          <h2 className="text-center font-heading text-2xl font-bold text-[#09292D]">Frequently asked</h2>
          <div className="mt-6 space-y-3">
            {(offer?.faq || []).map((item, i) => (
              <details key={i} className="group rounded-xl border border-slate-200 bg-[#F5F7F2] p-4">
                <summary className="flex cursor-pointer items-center gap-2 font-semibold text-[#09292D]">
                  <HelpCircle className="h-4 w-4 text-[#4e8a1f]" /> {item.q}
                </summary>
                <p className="mt-2 pl-6 text-sm text-slate-600">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-[#09292D] py-8 text-center text-xs text-white/50">
        <p>LIASROOTS pilot · operated by {offer?.legal_entity || "[Legal entity name — to be provided]"}</p>
        <p className="mt-1">Privacy: {offer?.privacy_url} · DPO: {offer?.dpo_email}</p>
      </footer>

      {/* Mobile sticky CTA */}
      {state !== "closed" && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-white/10 bg-[#09292D] p-3 sm:hidden">
          <button onClick={scrollToForm} className="w-full rounded-full py-3 text-sm font-bold text-[#09292D]" style={{ backgroundColor: LIME }}>
            {ctaLabel}
          </button>
        </div>
      )}
    </div>
  );
}
